/**
 * Dev Mail - Watch Command
 * Poll inbox every N seconds and display new emails automatically
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { ConfigService } from '../../../services/config.service.js';
import { TempMailApiService } from '../../../services/temp-mail.service.js';

interface WatchOptions {
    interval: string;
    timeout?: string;
    keep?: boolean;
    json?: boolean;
}

function htmlToText(html: string[]): string {
    return html
        .join('\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&[a-z]+;/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

async function handleWatch(address: string, options: WatchOptions): Promise<void> {
    const config = await new ConfigService().load();
    if (!config) {
        console.error('❌ Not initialized. Run "jai1 auth" first.');
        process.exit(1);
    }

    const intervalSec = Math.max(5, parseInt(options.interval, 10) || 15);
    const DEFAULT_TIMEOUT = 300; // 5 minutes
    const MAX_TIMEOUT = 1800; // 30 minutes
    const timeoutSec = Math.min(
        options.timeout ? parseInt(options.timeout, 10) || DEFAULT_TIMEOUT : DEFAULT_TIMEOUT,
        MAX_TIMEOUT
    );
    const api = new TempMailApiService(config);

    const seenIds = new Set<string>();
    let pollCount = 0;
    const startTime = Date.now();

    if (!options.json) {
        console.log(chalk.bold.cyan(`👀 Watching inbox: ${address}`));
        console.log(chalk.dim(
            `   Polling every ${intervalSec}s` +
            ` · timeout ${timeoutSec}s` +
            (options.keep ? ' · keep watching' : ' · stops after first email') +
            ` · Ctrl+C to stop\n`
        ));
    }

    // Initial fetch to seed seen IDs (don't display existing emails)
    const init = await api.getInbox(address, 30);
    if (init.success && init.data) {
        init.data.forEach((m) => seenIds.add(m.id));
    }

    const poll = async (): Promise<void> => {
        if (timeoutSec && (Date.now() - startTime) / 1000 >= timeoutSec) {
            if (!options.json) console.log(chalk.dim('\n⏱  Timeout reached. Stopped.'));
            process.exit(0);
        }

        pollCount++;

        if (!options.json) {
            process.stdout.write(chalk.dim(`\r  🔄 Poll #${pollCount} — ${new Date().toLocaleTimeString('vi-VN')}  `));
        }

        const result = await api.getInbox(address, 30);

        if (!result.success) {
            if (!options.json) {
                process.stdout.write(chalk.red(`\r  ❌ Error: ${result.error}\n`));
            }
            return;
        }

        const newMessages = (result.data ?? []).filter((m) => !seenIds.has(m.id));

        if (newMessages.length > 0) {
            for (const msg of newMessages) {
                seenIds.add(msg.id);

                if (options.json) {
                    console.log(JSON.stringify({ event: 'new_message', data: msg }));
                    continue;
                }

                // Fetch full message for body
                const detail = await api.getMessage(address, msg.id);

                process.stdout.write('\r' + ' '.repeat(50) + '\r'); // clear spinner
                console.log('\n' + chalk.bold.green('📨 New Email!\n'));
                console.log(`  ${chalk.bold('From:')}    ${msg.from.name || msg.from.address}`);
                console.log(`  ${chalk.bold('Subject:')} ${msg.subject || '(no subject)'}`);
                console.log(`  ${chalk.bold('Date:')}    ${new Date(msg.createdAt).toLocaleString('vi-VN')}`);
                console.log(`  ${chalk.dim('ID:')}      ${msg.id}`);

                if (detail.success && detail.data) {
                    const body = detail.data.text || (detail.data.html?.length ? htmlToText(detail.data.html) : '');
                    if (body) {
                        const preview = body.slice(0, 600) + (body.length > 600 ? '\n…' : '');
                        console.log('\n' + '─'.repeat(60) + '\n');
                        console.log(preview);
                        console.log('─'.repeat(60));
                    }
                }

                console.log();

                // Stop after first email unless --keep
                if (!options.keep) {
                    clearInterval(timer);
                    process.exit(0);
                }
            }
        }
    };

    // Run poll immediately, then on interval
    await poll();
    const timer = setInterval(poll, intervalSec * 1000);

    // Graceful shutdown
    process.on('SIGINT', () => {
        clearInterval(timer);
        if (!options.json) {
            console.log(chalk.dim('\n\n  Stopped watching. Goodbye!'));
        }
        process.exit(0);
    });
}

export function createMailWatchCommand(): Command {
    return new Command('watch')
        .description('Lắng nghe email mới (polling)')
        .argument('<address>', 'Địa chỉ email cần theo dõi')
        .option('-i, --interval <seconds>', 'Khoảng thời gian poll (giây, mặc định: 15)', '15')
        .option('-t, --timeout <seconds>', 'Dừng sau N giây (mặc định: 300s / 5 phút, tối đa: 1800s / 30 phút)')
        .option('-k, --keep', 'Tiếp tục watch sau khi nhận được email (mặc định: dừng sau email đầu tiên)')
        .option('-j, --json', 'Output sự kiện email mới dạng JSON (mỗi dòng 1 event)')
        .addHelpText('after', `
Examples:
  $ j dev mail watch user@dollicons.com           # Dừng khi nhận được email đầu tiên (timeout 5 phút)
  $ j dev mail watch user@dollicons.com -k        # Tiếp tục watch
  $ j dev mail watch user@dollicons.com -i 30 -k
  $ j dev mail watch user@dollicons.com -t 600     # Timeout 10 phút (tối đa 30 phút)
  $ j dev mail watch user@dollicons.com -j | jq .
`)
        .action(async (address: string, options: WatchOptions) => {
            await handleWatch(address, options);
        });
}
